import axios from 'axios';

import { useAuthStore } from '../store/auth';

/**
 * Single Axios instance for all authenticated API calls.
 *
 * - baseURL comes from VITE_API_BASE: in local dev this is `/api/v1` and the
 *   Vite proxy forwards to the backend; in staging/prod it's the absolute
 *   URL of the backend (cross-origin to the PWA's host).
 * - Request interceptor attaches the access token if we have one.
 * - Response interceptor catches 401s and tries a single /auth/refresh
 *   round-trip before failing — concurrent failures share one refresh
 *   promise so a burst of 401s doesn't stampede the refresh endpoint.
 *
 * The login page itself uses a separate, un-intercepted axios instance
 * (see api/auth.js) because /auth/login should never auto-refresh.
 */

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

// ── Attach access token to outgoing requests ────────────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Auto-refresh on 401 ─────────────────────────────────────────────────────
let refreshPromise = null;

async function refreshAccessToken() {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) throw new Error('No refresh token');

  // Use a bare axios call (not apiClient) so this isn't itself intercepted.
  const response = await axios.post(
    `${import.meta.env.VITE_API_BASE || '/api/v1'}/auth/refresh`,
    { refresh_token: refreshToken },
  );
  useAuthStore.setState({
    accessToken: response.data.access_token,
    refreshToken: response.data.refresh_token,
  });
  return response.data.access_token;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry
    ) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      } catch (refreshErr) {
        // Refresh failed — clear state and bounce the user to /login.
        useAuthStore.getState().clear();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
